import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponseWithData } from "../models/system";
import { CreateUpdateListingRuleRequest, IListingRule } from "../models/ListingRule";

export default function useListingRules(categoryId?: string) {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();
  const staleTime = 1000 * 60 * 10;

  const listingRules = useQuery({
    queryKey: ["listingRules"],
    queryFn: () => get<DefaultResponseWithData<IListingRule[]>>("/listingrule"),
    staleTime,
  });

  const ruleCategories = useQuery({
    queryKey: ["listingRuleCategories", categoryId],
    queryFn: () => get<DefaultResponseWithData<string[]>>(`/listingrule/${categoryId}/categories`),
    enabled: !!categoryId,
    staleTime,
  });

  const create = useMutation(
    (req: CreateUpdateListingRuleRequest) => post(`/listingrule`, req),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["listingRules"]);
      },
    }
  );

  const update = useMutation(
    (params: { id: string; req: CreateUpdateListingRuleRequest }) =>
      put(`/listingrule/${params.id}`, params.req),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["listingRules"]);
      },
    }
  );

  const remove = useMutation(
    (id: string) => del(`/listingrule/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["listingRules"]);
      },
    }
  );

  const setCategories = useMutation(
    (params: { id: string; categoryIds: string[] }) =>
      put(`/listingrule/${params.id}/categories`, params.categoryIds),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(["listingRules"]);
        queryClient.invalidateQueries(["listingRuleCategories", variables.id]);
      },
    }
  );

  return { listingRules, ruleCategories, create, update, remove, setCategories };
}


